import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import React from 'react'
import ReactDOM from 'react-dom'
import { Global, css } from '@emotion/core'
import 'antd/dist/antd.css'
import { Provider } from 'react-redux'
import { AuthProvider } from '@/contexts/auth'
import { AccountProvider } from '@/contexts/account'
import { StaffMutationGuardProvider } from '@/contexts/staff-mutation-guard'
import { MiwaAppWebsocketProvider } from '@/contexts/miwa-app-websocket'
import { store } from '@/features/store'
import { Router } from '@/components/apps/router'
import { ErrorBoundary } from '@/components/apps/error-boundary'
import { LoaderProvider } from './contexts/loader'
import { PrinterProvider } from './contexts/printer'
import { PushNotificationProvider } from './contexts/push-notification'
import { isStaffMutationGuardControlError } from '@/libs/errors'

/** axios 呼び出し側に .catch が無い場合でも、スタッフガードの意図的な中断を Unhandled Rejection にしない */
function staffMutationGuardUnhandledRejectionSilencer(event: PromiseRejectionEvent) {
  if (isStaffMutationGuardControlError(event.reason)) {
    event.preventDefault()
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', staffMutationGuardUnhandledRejectionSilencer)
}

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <AuthProvider>
          <AccountProvider>
            <StaffMutationGuardProvider>
              <MiwaAppWebsocketProvider>
                <PushNotificationProvider>
                  <PrinterProvider>
                    <LoaderProvider>
                      <Router />
                    </LoaderProvider>
                  </PrinterProvider>
                </PushNotificationProvider>
              </MiwaAppWebsocketProvider>
            </StaffMutationGuardProvider>
          </AccountProvider>
        </AuthProvider>
      </Provider>
    </ErrorBoundary>
  )
}

ReactDOM.render(
  <React.StrictMode>
    <link rel="stylesheet" href={require('@/static/styles/reset.css')} />
    <Global
      styles={css`
        @import url('https://fonts.googleapis.com/css?family=Lato');
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP');
        * {
          box-sizing: border-box;
          font-family: '游ゴシック体', YuGothic, '游ゴシック', 'Yu Gothic', 'Lato', sans-serif;
          letter-spacing: 0.9px;
          outline: 0;
          text-decoration: none;
        }
        @media only screen and (max-width: 599px) {
          * {
            font-family: 'Noto Sans JP', 'Lato', sans-serif;
          }
        }
        *::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        *::-webkit-scrollbar-track {
          background: transparent;
        }
        *::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 8px;
        }
        a {
          text-decoration: none;
        }
        li {
          list-style: none;
        }
        input,
        select {
          letter-spacing: 0.9px;
        }
      `}
    />
    <link rel="stylesheet" href={require('@/static/styles/datepicker.css')} />
    <link rel="stylesheet" href={require('@/static/styles/datepicker-custom.css')} />
    <link rel="stylesheet" href={require('@/static/styles/cascader-antd-custom.css')} />
    <link rel="stylesheet" href={require('@/static/styles/select-antd-custom.css')} />
    <App />
  </React.StrictMode>,
  document.getElementById('root'),
)
